import axios from 'axios';
import { toast } from 'react-toastify';

// 로그아웃 콜백 함수를 저장할 변수
let logoutCallback = () => { };

// 로그아웃 콜백 함수를 설정하는 함수
export const setLogoutCallback = (callback) => {
    logoutCallback = callback;
};

const BASE_URL = process.env.REACT_APP_BOOT_URL;
const LOGIN_URL = '/login';

const AI_TIMEOUT = 30000;
const BIZ_LLM_TIMEOUT = 180000;
const DEFAULT_TIMEOUT = 10000;

const axios_api = axios.create({
    // baseURL: `${BASE_URL}/api`,
    baseURL: '/api',
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT,
    headers: {
        Accept: 'application/json',
    },
});

// ---------------------------------------------------------------------
// 에러 알림 캐시 설정 (예: 15초 창, 최대 1회)
// ---------------------------------------------------------------------
const errorNotificationCache = {};
const ERROR_NOTIFICATION_TIME_WINDOW = 15000; // 15초
const MAX_ERROR_COUNT = 1;

function shouldShowError(errorMessage) {
    const now = Date.now();
    const cacheEntry = errorNotificationCache[errorMessage];
    if (!cacheEntry) {
        // 캐시 항목이 없으면 새로 생성하고 표시
        errorNotificationCache[errorMessage] = { count: 1, lastShown: now };
        return true;
    } else {
        // 동일 에러가 지난 시간 창을 넘었으면 초기화
        if (now - cacheEntry.lastShown > ERROR_NOTIFICATION_TIME_WINDOW) {
            errorNotificationCache[errorMessage] = { count: 1, lastShown: now };
            return true;
        } else {
            // 시간 창 내에 표시된 횟수가 MAX_ERROR_COUNT보다 작으면 표시
            if (cacheEntry.count < MAX_ERROR_COUNT) {
                cacheEntry.count += 1;
                cacheEntry.lastShown = now;
                return true;
            } else {
                return false;
            }
        }
    }
}

axios_api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('jwt');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        if (/^\/ai-/.test(config.url) && config.timeout < AI_TIMEOUT) {
            config.timeout = AI_TIMEOUT;
        }
        if (/\/bizscanpro\/cards\/\d+\/(analyze|process)/.test(config.url)) {
            config.timeout = BIZ_LLM_TIMEOUT;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function getHttpErrorMessage(status) {
    switch (status) {
        case 400:
            return '잘못된 요청입니다.';
        case 403:
            return '접근 권한이 거부되었습니다.';
        case 404:
            return '요청한 페이지를 찾을 수 없습니다.';
        case 500:
            return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        default:
            return '알 수 없는 오류가 발생했습니다.';
    }
}

axios_api.interceptors.response.use(
    (response) => {
        const data = response.data;
        const suppressToast = !!response?.config?.suppressToast;
        if (data && typeof data === 'object') {
            // 비즈니스 에러(status=false)인 경우
            if (data.status === false) {
                const errMsg = data.message || '알 수 없는 오류가 발생했습니다.';
                if (!suppressToast) {
                    toast.error(errMsg);
                }
                // throw해서 호출부의 catch로 이동
                const errorObj = new Error(errMsg);
                errorObj.responseData = data;
                throw errorObj;
            }
        }
        return response;
    },
    (error) => {
        if (error.response) {
            const { status, data, config } = error.response;
            const suppressToast = !!config?.suppressToast;

            // 개발 환경 로그
            if (process.env.NODE_ENV === 'development') {
                console.error('API 에러 발생:', data);
            }

            // 메시지 설정
            if (data && typeof data === 'object' && data.message) {
                error.message = data.message;
            } else {
                error.message = getHttpErrorMessage(status);
            }
            // React Query 소비자가 error.status로 판단할 수 있도록 노출
            error.status = status;

            // 401 처리
            if (status === 401) {
                if (config.url === LOGIN_URL) {
                    const errorMessage = data.error || '아이디나 비밀번호가 다릅니다.';
                    if (!suppressToast && shouldShowError(errorMessage)) {
                        toast.error(errorMessage);
                    }
                } else if (localStorage.getItem('jwt')) {
                    // JWT가 있는데 401이면 진짜 세션 만료
                    logoutCallback(true);
                }
                // JWT가 이미 없으면 로그아웃 중 in-flight 요청 → 무시
            } else if (status === 403 && config.method === 'get') {
                // GET 403: PermissionErrorBoundary가 콘텐츠 영역에서 처리하므로 toast 억제
                // (POST/PUT/DELETE 403은 아래 else에서 toast 그대로 표시)
            } else {
                if (!suppressToast && shouldShowError(error.message)) {
                    toast.error(error.message);
                }
            }
        } else if (error.request) {
            console.error('응답을 받지 못했습니다:', error.request);
            if (!error?.config?.suppressToast && shouldShowError('서버와의 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.')) {
                toast.error('서버와의 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.');
            }
        } else {
            console.error('에러 발생:', error.message);
            if (!error?.config?.suppressToast && shouldShowError('알 수 없는 오류가 발생했습니다.')) {
                toast.error('알 수 없는 오류가 발생했습니다.');
            }
        }

        return Promise.reject(error);
    }
);

export const get = async (url, config = {}) => {
    const response = await axios_api.get(url, config);
    return response.data;
};

export const post = async (url, data, config = {}) => {
    const response = await axios_api.post(url, data, config);
    return response.data;
};

export const put = async (url, data, config = {}) => {
    const response = await axios_api.put(url, data, config);
    return response.data;
};

export const del = async (url, config = {}) => {
    const response = await axios_api.delete(url, config);
    return response.data;
};

export default axios_api;
